// What the pages that read the audit trail share: the query a page at a
// time with its CSV export, the date range, how a party is named, and the
// drawer that shows one event as a page of the record. The Audit trail and
// the Sharing audit are both built from these.

import { ActionIcon, Badge, Box, CopyButton, Group, Text, type MantineColor } from '@mantine/core';
import { DatePickerInput, type DatePickerPreset, type DatesRangeValue } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { timestampFromDate } from '@bufbuild/protobuf/wkt';
import type { AuditEvent } from '@motionsafe/ms-console-api/admin/v1/audit_pb.js';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { Fragment, useState, type ReactNode } from 'react';

import { api } from '../api/client';
import { errorText } from '../api/hooks';
import { localDay, when } from '../lib/format';
import { DrawerSection } from './page';
import { useServerPages } from './pager';
import { PaneBody, PaneHeader } from './pane';

export const outcomeColor = (o: string): MantineColor => (o === 'ok' ? 'success' : o === 'refused' ? 'danger' : 'warning');

// The raw action is what admins search the audit vocabulary by, so it stays
// verbatim: monospace, a step below body size so the column stays calm.
export const ACTION_STYLE = { fontSize: 'var(--mantine-font-size-sm)' } as const;
export const WRAP = { whiteSpace: 'normal', overflowWrap: 'anywhere' } as const;
// An actor or target known only by its id: whole, never truncated, and free
// to wrap when the table is narrower than every id at full length.
const PARTY_ID_STYLE = { ...ACTION_STYLE, ...WRAP } as const;

export const HAIRLINE = '1px solid var(--mantine-color-default-border)';

// The ranges an administrator reaches for first, each ending today.
function rangePresets(): DatePickerPreset<'range'>[] {
  const today = dayjs();
  const day = (d: dayjs.Dayjs) => d.format('YYYY-MM-DD');
  return [
    { value: [day(today.subtract(6, 'day')), day(today)], label: 'Last 7 days' },
    { value: [day(today.subtract(29, 'day')), day(today)], label: 'Last 30 days' },
  ];
}

/** The dates a trail is narrowed to, start to end, as the person's own days. */
export function AuditRange({ value, onChange }: { value: DatesRangeValue<string>; onChange: (v: DatesRangeValue<string>) => void }) {
  return (
    <DatePickerInput
      type="range"
      value={value}
      onChange={onChange}
      presets={rangePresets()}
      allowSingleDateInRange
      clearable
      valueFormat="MMM D, YYYY"
      labelSeparator="to"
      placeholder="Any date"
      aria-label="Date range"
      w={{ base: '100%', xs: 280 }}
      classNames={{ input: 'num' }}
    />
  );
}

type Party = { type: string; id: string; name: string };

export function None() {
  return <Text span c="dimmed" inherit>none</Text>;
}

// Who or what, by the best name the event carries: a name, else the id, else the kind.
export function PartyCell({ party }: { party: Party | undefined }) {
  if (!party) return <None />;
  if (party.name) return <>{party.name}</>;
  if (party.id) return <span className="id" style={PARTY_ID_STYLE}>{party.id}</span>;
  return party.type ? <>{party.type}</> : <None />;
}

/**
 * A trail a page at a time, newest first, with its export: the organization
 * in view, or with `top` every organization, for staff on their own pages.
 * A changed organization, set of actions, or range starts again from the
 * first page.
 */
export function useAuditPages({ top, orgId, actions, range }: { top: boolean; orgId: string; actions: string[]; range: DatesRangeValue<string> }) {
  // A range applies once both ends are picked; closing the picker halfway
  // clears it, so the list never refetches for half a range.
  const [from, to] = range[0] && range[1] ? [range[0], range[1]] : ['', ''];
  // Both ends of the range are the person's own days, start to end.
  const fromDate = localDay(from, 'start');
  const toDate = localDay(to, 'end');
  const query = { orgId: top ? '' : orgId, allOrganizations: top, actions, from: fromDate ? timestampFromDate(fromDate) : undefined, to: toDate ? timestampFromDate(toDate) : undefined };
  const narrowed = `${top ? 'all' : orgId}|${actions.join(',')}|${from}|${to}`;
  const pages = useServerPages({ key: ['audit'], narrowed, read: (page) => api.audit.queryAudit({ ...query, ...page }), noun: 'events' });
  const events = pages.query;
  const [exporting, setExporting] = useState(false);
  // The whole trail the page is narrowed to, as CSV; `withActions` widens what it carries.
  const exportCsv = async (withActions: string[] = actions) => {
    setExporting(true);
    try {
      const r = await api.audit.exportAudit({ query: { ...query, actions: withActions, pageSize: 0, offset: 0 }, format: 'csv' });
      window.location.assign(r.downloadUrl);
    } catch (e) {
      notifications.show({ color: 'danger', message: errorText(e) });
    } finally {
      setExporting(false);
    }
  };
  return {
    events,
    rows: events.data?.events ?? [],
    total: pages.total,
    page: pages.page,
    setPage: pages.setPage,
    dated: !!from || !!to,
    query,
    exportCsv,
    exporting,
  };
}

// An identifier, whole, with the means to carry it: set in the mono face
// and free to wrap, its copy control beside it.
function Id({ value, label }: { value: string; label: string }) {
  return (
    <Group gap={6} wrap="nowrap" align="flex-start">
      <Text span size="sm" className="id" style={WRAP} miw={0}>{value}</Text>
      <CopyButton value={value} timeout={2000}>
        {({ copied, copy }) => (
          <ActionIcon variant="subtle" color="gray" size={22} onClick={copy} aria-label={copied ? `${label} copied` : `Copy ${label.toLowerCase()}`} style={{ flex: 'none' }}>
            {copied ? <IconCheck size={14} stroke={1.75} /> : <IconCopy size={14} stroke={1.75} />}
          </ActionIcon>
        )}
      </CopyButton>
    </Group>
  );
}

// One part of the record, read from the left: what each line is in the
// secondary ink, what it says beside it. Lines with nothing to say are left out.
function Terms({ rows }: { rows: ([string, ReactNode] | null | false | undefined | '')[] }) {
  const shown = rows.filter((r): r is [string, ReactNode] => !!r && r[1] !== '' && r[1] !== null && r[1] !== undefined);
  return (
    <Box component="dl" m={0} style={{ display: 'grid', gridTemplateColumns: '7.5rem minmax(0, 1fr)', columnGap: 'var(--mantine-spacing-md)', rowGap: 8 }}>
      {shown.map(([label, value]) => (
        <Fragment key={label}>
          <Text component="dt" size="sm" c="dimmed">{label}</Text>
          <Text component="dd" size="sm" m={0} style={WRAP}>{value}</Text>
        </Fragment>
      ))}
    </Box>
  );
}

// A key of the event's own detail as a person reads it: `hardware_id` is "Hardware id".
const words = (key: string) => { const w = key.replace(/_/g, ' '); return w.charAt(0).toUpperCase() + w.slice(1); };

// What a detail value says: a word or a number as it is, anything with structure as its JSON.
function detailValue(v: unknown): ReactNode {
  if (v === null || v === undefined || v === '') return <None />;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return <span className="num">{v}</span>;
  if (typeof v === 'string') return /^[A-Za-z0-9_.:@+-]{12,}$/.test(v) ? <span className="id">{v}</span> : v;
  // A short list of words reads as words; anything deeper is shown as it was recorded.
  if (Array.isArray(v) && v.every((x) => typeof x === 'string' || typeof x === 'number')) return v.length ? v.join(', ') : <None />;
  return <span className="id">{JSON.stringify(v)}</span>;
}

// One event, as a page of the record. The head says it at a glance: what was
// done, how it ended, when, and by whom. Under it, who and what it was done
// to, where it came from, what the event itself recorded, and the two
// identifiers that tie it into the chain. `extra` is a page's own section,
// shown after what the event recorded.
export function EventDetail({ e, extra }: { e: AuditEvent; extra?: ReactNode }) {
  const actor = e.actor?.name || (e.actor?.type === 'system' ? 'the system' : e.actor?.type) || 'unknown';
  const detail = e.detail && typeof e.detail === 'object' ? Object.entries(e.detail as Record<string, unknown>) : [];
  // A device says when by its own clock; the console's receipt is the time to trust.
  const fromDevice = e.source === 'device';
  return (
    <>
      <PaneHeader
        mono
        title={e.action}
        subtitle={<>{when(e.occurredAt)} by {actor}</>}
        state={<Badge color={outcomeColor(e.outcome)}>{e.outcome}</Badge>}
      />
      <PaneBody>
        <DrawerSection title="Who">
          <Terms rows={[
            ['Name', e.actor?.name || <None />],
            e.actor?.type && ['Kind', e.actor.type],
            e.actor?.id && ['ID', <Id key="id" value={e.actor.id} label="Actor ID" />],
          ]} />
        </DrawerSection>
        {e.target && (e.target.name || e.target.id) && (
          <DrawerSection title="Done to">
            <Terms rows={[
              ['Name', e.target.name || <None />],
              e.target.type && ['Kind', e.target.type],
              e.target.id && ['ID', <Id key="id" value={e.target.id} label="Target ID" />],
            ]} />
          </DrawerSection>
        )}
        <DrawerSection title="Where from">
          <Terms rows={[
            ['Source', e.source || <None />],
            e.deviceId && ['Device', <Id key="d" value={e.deviceId} label="Device record ID" />],
            e.ip && ['IP address', <span key="ip" className="id">{e.ip}</span>],
            e.userAgent && ['Browser or app', e.userAgent],
            fromDevice && ['Received', when(e.receivedAt)],
          ]} />
          {fromDevice && <Text size="xs" c="dimmed">The time at the top is the device&apos;s own clock, which is not trusted. Received is when the console recorded it.</Text>}
        </DrawerSection>
        {detail.length > 0 && (
          <DrawerSection title="What it recorded">
            <Terms rows={detail.map(([k, v]) => [words(k), detailValue(v)] as [string, ReactNode])} />
          </DrawerSection>
        )}
        {extra}
        <DrawerSection title="In the record">
          <Terms rows={[
            ['Event ID', <Id key="e" value={e.id} label="Event ID" />],
            ['Hash', <Id key="h" value={e.hash} label="Hash" />],
          ]} />
        </DrawerSection>
      </PaneBody>
    </>
  );
}

/** The record of one event's facts, for a page's own drawer section. */
export function EventTerms({ rows }: { rows: ([string, ReactNode] | null | false | undefined | '')[] }) {
  return <Terms rows={rows} />;
}
